import { createAsyncThunk } from '@reduxjs/toolkit';

import { BinType, InboundTaskItemAssignType } from 'commons/enums';
import { PRIMARY_BLUE, PRIMARY_GREEN } from 'commons/styles/colors';
import {
  IBin,
  IInboundTaskItem,
  IInboundTaskProcessedItem,
  ProductDetail,
} from 'commons/types';
import { defaultActionProcess } from 'commons/utils/defaultActionProcess';
import { RootReducerInterface } from 'redux-stores/reducers';
import {
  setInboundInvoice,
  updateInboundInvoiceItem,
} from 'redux-stores/reducers/inboundInvoiceReducer';
import { setInboundTaskItemAssignStatus } from 'redux-stores/reducers/inboundTaskDocumentReducer';
import {
  setAssignedInboundTaskItem,
  setInboundTaskBinRecommendationInfo,
  setAssignedInboundTaskReceiveActual,
  setInboundTaskReceiveItemActual,
} from 'redux-stores/reducers/inboundTaskItemReducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';
import { swipeRxWmsApiInboundInvoice } from 'services/apis/SwipeRxWmsApiInboundInvoice';
import { swipeRxWmsApiInboundTaskItem } from 'services/apis/SwipeRxWmsApiInboundTaskItem';
import {
  BulkCheckInboundTaskItemRequest,
  CheckInboundTaskItemRequest,
  InboundTaskItemAssignUnassignRequest,
  PostBulkUnassignItemByInboundTaskNumberRequest,
} from 'services/apis/SwipeRxWmsApiInboundTaskItem/type';

export const actionAssignUnassignInboundTaskItem = createAsyncThunk(
  'inboundTaskItem/actionAssignUnassignInboundTaskItem',
  async (
    params: {
      item: IInboundTaskItem;
      assignType: InboundTaskItemAssignType;
      username: string;
    },
    { dispatch, getState },
  ) => {
    await defaultActionProcess(async () => {
      const { item, assignType, username } = params;
      const { inboundInvoice: inboundInvoiceState } =
        getState() as RootReducerInterface;
      const { inboundInvoice } = inboundInvoiceState;
      const assignPayload: InboundTaskItemAssignUnassignRequest = {
        inbound_task_number: item.inbound_task_number,
        sku_code: item.sku_code,
        foc: item.foc,
      };

      if (assignType === InboundTaskItemAssignType.ASSIGN) {
        await swipeRxWmsApiInboundTaskItem.assignTask(assignPayload);
        const newInvoiceItems = [...(inboundInvoice?.items || []), item];
        dispatch(setAssignedInboundTaskItem(newInvoiceItems));
        if (inboundInvoice) {
          dispatch(
            setInboundInvoice({
              ...inboundInvoice,
              items: newInvoiceItems,
            }),
          );
        }
      } else {
        await swipeRxWmsApiInboundTaskItem.unassignTask(assignPayload);
        const newInvoiceItems = inboundInvoice?.items?.filter(
          (invoiceItem) =>
            invoiceItem.sku_code !== item.sku_code ||
            invoiceItem.foc !== item.foc,
        );
        dispatch(setAssignedInboundTaskItem(newInvoiceItems || []));
        if (inboundInvoice) {
          if (newInvoiceItems?.length === 0) {
            dispatch(setInboundInvoice(null));
          } else {
            dispatch(
              setInboundInvoice({
                ...inboundInvoice,
                items: newInvoiceItems,
              }),
            );
          }
        }
      }
      dispatch(
        setInboundTaskItemAssignStatus({
          assignPayload,
          assignType,
          username,
        }),
      );
    }, dispatch);
    return undefined;
  },
);

export const actionBulkUnassignTaskItemByInboundTaskNumber = createAsyncThunk(
  'inboundTaskItem/actionBulkUnassignTaskItemByInboundTaskNumber',
  async (
    payload: PostBulkUnassignItemByInboundTaskNumberRequest,
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      await swipeRxWmsApiInboundTaskItem.bulkUnassignItemByInboundTaskNumber(
        payload,
      );
    }, dispatch);
  },
);
export const actionPostReceiveActual = createAsyncThunk(
  'inboundTaskItem/actionPostReceiveActual',
  async (
    params: {
      inboundTaskNumber: string;
      skuCode: string;
      foc: boolean;
      receiveActual: Pick<
        ProductDetail,
        'bin' | 'bin_type' | 'inventory_number' | 'expiry_date' | 'quantity'
      >;
    },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { inboundTaskNumber, skuCode, foc, receiveActual } = params;
      await swipeRxWmsApiInboundTaskItem.postReceiveItem({
        inbound_task_number: inboundTaskNumber,
        sku_code: skuCode,
        item: receiveActual,
        foc,
      });
      dispatch(
        setAssignedInboundTaskReceiveActual({
          isRemove: false,
          item: {
            ...receiveActual,
            expiry_date: receiveActual.expiry_date.toISOString(),
          },
        }),
      );
      dispatch(
        setInboundTaskReceiveItemActual({
          isRemove: false,
          item: {
            ...receiveActual,
            expiry_date: receiveActual.expiry_date.toISOString(),
          },
        }),
      );
    }, dispatch);
    return undefined;
  },
);

export const actionPostInboundTaskRemoveBatch = createAsyncThunk(
  'inboundTaskItem/actionPostInboundTaskRemoveBatch',
  async (
    params: {
      inboundTaskNumber: string;
      skuCode: string;
      foc: boolean;
      removedActual: IInboundTaskProcessedItem;
    },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const { inboundTaskNumber, skuCode, foc, removedActual } = params;
      await swipeRxWmsApiInboundTaskItem.postRemoveBatch({
        inbound_task_number: inboundTaskNumber,
        sku_code: skuCode,
        bin_number: removedActual.bin,
        inventory_number: removedActual.inventory_number,
        foc,
      });
      dispatch(
        setAssignedInboundTaskReceiveActual({
          isRemove: true,
          item: removedActual,
        }),
      );
      dispatch(
        setInboundTaskReceiveItemActual({
          isRemove: true,
          item: removedActual,
        }),
      );
    }, dispatch);
    return undefined;
  },
);

export const getInboundTaskItemBinRecommendationInfo = createAsyncThunk(
  'inboundTaskItem/getInboundTaskItemBinRecommendationInfo',
  async (params: { skuCode: string; binType: BinType }, { dispatch }) => {
    const { skuCode, binType } = params;
    defaultActionProcess(async () => {
      const result =
        await swipeRxWmsApiInboundTaskItem.getBinRecommendationInfo(
          skuCode,
          binType,
        );
      dispatch(setInboundTaskBinRecommendationInfo(result));
    }, dispatch);
  },
);

export const checkInboundTaskItemAction = createAsyncThunk(
  'inboundTaskItem/checkInboundTaskItemAction',
  async (params: CheckInboundTaskItemRequest, { dispatch }) => {
    defaultActionProcess(async () => {
      await swipeRxWmsApiInboundTaskItem.checkInboundTaskItem(params);
      // Refetch the invoice to get updated item status
      const updatedInvoice =
        await swipeRxWmsApiInboundInvoice.getWaitingApprovalInboundInvoice({
          inbound_task_number: params.inbound_task_number,
          invoice_number: params.invoice_number,
        });
      const updatedItem = updatedInvoice.items?.find(
        (item) => item.sku_code === params.sku_code && item.foc === params.foc,
      );
      if (updatedItem) {
        dispatch(updateInboundInvoiceItem(updatedItem));
      }
    }, dispatch);
    return undefined;
  },
);

export const bulkCheckInboundTaskItemAction = createAsyncThunk(
  'inboundTaskItem/bulkCheckInboundTaskItemAction',
  async (params: BulkCheckInboundTaskItemRequest, { dispatch }) => {
    defaultActionProcess(async () => {
      const result =
        await swipeRxWmsApiInboundTaskItem.bulkCheckInboundTaskItem(params);

      if (result.skipped.length === 0) {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Bulk ${
              params.approved ? 'approve' : 'reject'
            } success! \n ${result.success.join('\n')}`,
            color: PRIMARY_GREEN,
          }),
        );
      } else {
        dispatch(
          snackbarSetData({
            open: true,
            message: `Bulk ${
              params.approved ? 'approve' : 'reject'
            } item success: \n ${result.success.join(
              '\n',
            )} \n while item skipped: \n ${result.skipped.join('\n')}`,
            color: PRIMARY_BLUE,
          }),
        );
      }

      // update state - refetch invoice to get updated statuses
      const updatedInvoice =
        await swipeRxWmsApiInboundInvoice.getWaitingApprovalInboundInvoice({
          inbound_task_number: params.inbound_task_number,
          invoice_number: params.invoice_number,
        });
      dispatch(setInboundInvoice(updatedInvoice));
    }, dispatch);
  },
);

export const getValidateBin = async (
  params: { bin: string },
  dispatch: AppDispatch,
): Promise<IBin | null | undefined> => {
  const { bin } = params;
  return defaultActionProcess(async () => {
    const result = await swipeRxWmsApiInboundTaskItem.getValidateBin(bin);
    return result;
  }, dispatch);
};
